import React from 'react'
import PropTypes from 'prop-types'
import Link from '../../../../link'
import './WingsLink.css'

/**
 * WingsDisplay Component
 * 
 * Largest text style for hero sections and major headings.
 * Size: 73px / Line height: 52px / Weight: 800 (ExtraBold)
 * 
 * @component
 * @example
 * <WingsDisplay>Welcome to Wingspans</WingsDisplay>
 */
export const WingsDisplay = React.forwardRef(({ children, className = '', as: Component = 'h1', ...props }, ref) => {
  const classNames = ['w-display', className].filter(Boolean).join(' ')
  
  return (
    <Component ref={ref} className={classNames} {...props}>
      {children}
    </Component>
  )
})

WingsDisplay.displayName = 'WingsDisplay'

WingsDisplay.propTypes = {
  /** Text content */
  children: PropTypes.node.isRequired,
  
  /** HTML element to render as */
  as: PropTypes.string,
  
  /** Additional CSS classes */
  className: PropTypes.string,
  
  /** Inline styles to override defaults */
  style: PropTypes.object
}

/**
 * WingsEyebrow Component
 * 
 * Subtitle or section title text style.
 * Size: 18px / Letter spacing: 14%
 * 
 * @component
 * @example
 * <WingsEyebrow>Section Title</WingsEyebrow>
 * <WingsEyebrow color="blue">Blue Title</WingsEyebrow>
 */
export const WingsEyebrow = ({ children, className = '', color = 'blue', as: Component = 'h4', ...props }) => {
  const classNames = [
    'w-eyebrow',
    `w-eyebrow-${color}`,
    className
  ].filter(Boolean).join(' ')
  
  return (
    <Component className={classNames} {...props}>
      {children}
    </Component>
  )
}

WingsEyebrow.propTypes = {
  /** Text content */
  children: PropTypes.node.isRequired,
  
  /** Color variant: 'black', 'blue', or 'yellow' */
  color: PropTypes.oneOf(['black', 'blue', 'yellow']),
  
  /** HTML element to render as */
  as: PropTypes.string,
  
  /** Additional CSS classes */
  className: PropTypes.string,
  
  /** Inline styles to override defaults */
  style: PropTypes.object
}

/**
 * WingsHeading Component
 * 
 * Semantic heading component with level prop for H1/H2/H3.
 * Automatically renders the correct HTML element and applies appropriate styling.
 * 
 * @component
 * @example
 * <WingsHeading level={1}>Main Heading</WingsHeading>
 * <WingsHeading level={2}>Subheading</WingsHeading>
 * <WingsHeading level={3}>Section Heading</WingsHeading>
 */
export const WingsHeading = ({ children, level = 1, className = '', underlineColor, ...props }) => {
  const Component = `h${level}`
  const classNames = [`w-h${level}`, className, underlineColor && "w-heading-underlined"].filter(Boolean).join(' ')
  
  return (
    <Component className={classNames} {...props}>
      {children}
      {underlineColor && (
        <svg className="w-heading-underline" width="100%" viewBox="0 0 300 8" preserveAspectRatio="none">
          <path d="M 3 3 Q 150 1 297 4" stroke={underlineColor} strokeWidth="2.5" fill="none" strokeLinecap="round" />
        </svg>
      )}
    </Component>
  )
}

WingsHeading.propTypes = {
  /** Heading text */
  children: PropTypes.node.isRequired,
  
  /** Heading level (1–6) */
  level: PropTypes.oneOf([1, 2, 3, 4, 5, 6]).isRequired,
  
  /** Additional CSS classes */
  className: PropTypes.string,
  
  /** Inline styles to override defaults */
  style: PropTypes.object,

  /** If included, add colored underline beneath header */
  underlineColor: PropTypes.string,
}

/**
 * WingsBlockQuote Component
 * 
 * Blockquote text style for testimonials and quotes.
 * Size: 20px / Weight: 500 (Medium) / Style: Italic
 * 
 * @component
 * @example
 * <WingsBlockQuote>
 *   "Wingspans transformed my career path!"
 * </WingsBlockQuote>
 */
export const WingsBlockQuote = ({ children, className = '', ...props }) => {
  const classNames = ['w-blockquote', className].filter(Boolean).join(' ')
  
  return (
    <blockquote className={classNames} {...props}>
      {children}
    </blockquote>
  )
}

WingsBlockQuote.propTypes = {
  /** Quote content */
  children: PropTypes.node.isRequired,
  
  /** Additional CSS classes */
  className: PropTypes.string,
  
  /** Inline styles to override defaults */
  style: PropTypes.object
}

/**
 * WingsText Component
 * 
 * Standard body text for paragraphs and content.
 * Size: 18px desktop / 16px mobile / Line height: 1.5 / Weight: 400 (Regular)
 * 
 * @component
 * @example
 * <WingsText>
 *   This is a paragraph of body text with standard styling.
 * </WingsText>
 */
export const WingsText = ({ children, className = '', as: Component = 'p', ...props }) => {
  const classNames = ['w-text', className].filter(Boolean).join(' ')
  
  return (
    <Component className={classNames} {...props}>
      {children}
    </Component>
  )
}

WingsText.propTypes = {
  /** Text content */
  children: PropTypes.node.isRequired,
  
  /** HTML element to render as */
  as: PropTypes.string,
  
  /** Additional CSS classes */
  className: PropTypes.string,
  
  /** Inline styles to override defaults */
  style: PropTypes.object
}

/**
 * WingsDetailText Component
 * 
 * Smallest text style for labels, captions, and metadata.
 * Size: 16px / Line height: 16px / Weight: 400 (Regular)
 * 
 * @component
 * @example
 * <WingsDetailText>Created on Jan 15, 2024</WingsDetailText>
 */
export const WingsDetailText = ({ children, className = '', as: Component = 'span', ...props }) => {
  const classNames = ['w-label', className].filter(Boolean).join(' ')
  
  return (
    <Component className={classNames} {...props}>
      {children}
    </Component>
  )
}

WingsDetailText.propTypes = {
  /** Label text */
  children: PropTypes.node.isRequired,
  
  /** HTML element to render as */
  as: PropTypes.string,
  
  /** Additional CSS classes */
  className: PropTypes.string,
  
  /** Inline styles to override defaults */
  style: PropTypes.object
}

/**
 * WingsQuoteText Component
 *
 * Italic quote text in three sizes for testimonials and pull quotes.
 *
 * Sizes:
 * - large:  31px desktop / 25px mobile — Regular / Line height 1.5
 * - medium: 23px desktop / 20px mobile — Bold    / Line height 1.25
 * - small:  18px desktop / 16px mobile — Regular / Line height 1.5
 *
 * @component
 * @example
 * <WingsQuoteText size="large">"Wingspans changed my perspective."</WingsQuoteText>
 * <WingsQuoteText size="medium">"A bold testimonial."</WingsQuoteText>
 * <WingsQuoteText size="small">"Supporting detail quote."</WingsQuoteText>
 */
export const WingsQuoteText = ({ children, className = '', size = 'large', as: Component = 'p', ...props }) => {
  const classNames = ['w-quote', `w-quote-${size}`, className].filter(Boolean).join(' ')

  return (
    <Component className={classNames} {...props}>
      {children}
    </Component>
  )
}

WingsQuoteText.propTypes = {
  /** Quote content */
  children: PropTypes.node.isRequired,

  /** Size variant */
  size: PropTypes.oneOf(['large', 'medium', 'small']),

  /** HTML element to render as */
  as: PropTypes.string,

  /** Additional CSS classes */
  className: PropTypes.string,

  /** Inline styles to override defaults */
  style: PropTypes.object,
}

/**
 * WingsLink Component
 *
 * Anchor link styled to match any typography size in the design system.
 * Color is #40777C by default. Underline is applied for light/regular weights
 * (h1–h4, body, detail) and omitted for bold/extra-bold weights (display, h5, h6, eyebrow).
 *
 * Uses the Gatsby Link wrapper by default for internal navigation.
 * Pass isExternal for links outside the site, isNewTab to open in a new tab.
 * Pass onClick alone (without to) to use as a styled button-link with a custom callback.
 *
 * @component
 * @example
 * <WingsLink to="/about" size="body">Read more</WingsLink>
 * <WingsLink to="https://example.com" size="body" isExternal>Visit site</WingsLink>
 * <WingsLink to="https://example.com" size="body" isExternal isNewTab>Open in new tab</WingsLink>
 * <WingsLink size="body" onClick={openModal}>Open modal</WingsLink>
 * <WingsLink to="/about" size="body" onClick={handleClick}>Track and navigate</WingsLink>
 */
export const WingsLink = ({ children, className = '', size = 'body', display = 'inline', isExternal = false, isNewTab = false, onClick, to, ...props }) => {
  const classNames = ['w-link', `w-link-${size}`, display === 'block' ? 'w-link-block' : '', className].filter(Boolean).join(' ')
  const newTabProps = isNewTab ? { target: '_blank', rel: 'noopener noreferrer' } : {}

  // No destination — render as a button to keep semantics correct
  if (!to && onClick) {
    return (
      <button className={classNames} onClick={onClick} type="button" {...props}>
        {children}
      </button>
    )
  }

  if (isExternal) {
    return (
      <a className={classNames} href={to} onClick={onClick} {...newTabProps} {...props}>
        {children}
      </a>
    )
  }

  return (
    <Link className={classNames} to={to} onClick={onClick} {...newTabProps} {...props}>
      {children}
    </Link>
  )
}

WingsLink.propTypes = {
  /** Link content */
  children: PropTypes.node.isRequired,

  /**
   * Navigation destination — used as href for external links, to for internal.
   * Optional when onClick is provided as the sole interaction handler.
   */
  to: PropTypes.string,

  /** Callback fired on click. When provided without to, renders a button instead of an anchor. */
  onClick: PropTypes.func,

  /** Display mode — 'inline' (default) or 'block' */
  display: PropTypes.oneOf(['inline', 'block']),

  /**
   * Typography size variant — matches the size and weight of the corresponding text style.
   * Underline is applied for light/regular weights; omitted for bold/extra-bold.
   */
  size: PropTypes.oneOf(['display', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'body', 'detail', 'eyebrow']),

  /** Render as a plain <a> tag instead of the Gatsby Link wrapper */
  isExternal: PropTypes.bool,

  /** Open the link in a new tab (adds target="_blank" rel="noopener noreferrer") */
  isNewTab: PropTypes.bool,


  /** Additional CSS classes */
  className: PropTypes.string,

  /** Inline styles to override defaults */
  style: PropTypes.object
}

// Export all components
export default {
  WingsDisplay,
  WingsEyebrow,
  WingsHeading,
  WingsBlockQuote,
  WingsText,
  WingsDetailText,
  WingsLink
}
